import type { MetaFunction } from "@remix-run/cloudflare";
import { type Locale, type MessageKey, createTranslator, defaultLocale, getMessages, isLocale } from "../i18n";
import { socialMeta } from "./socialMeta";

const SITE_ORIGIN = "https://gsplat.org";
const DEFAULT_IMAGE = `${SITE_ORIGIN}/images/gsplat-share-social.jpg`;

type Robots = "index,follow" | "noindex,nofollow";

type LocalizedMetaOptions = {
	title: (locale: Locale) => string;
	description: (locale: Locale) => string;
	path: string;
	image?: string;
	robots?: Robots;
};

type TranslatedMetaOptions = {
	titleKey: MessageKey;
	descriptionKey: MessageKey;
	path: string;
	image?: string;
	robots?: Robots;
};

const localeFromData = (data: unknown, rootData: unknown): Locale => {
	for (const source of [data, rootData]) {
		const value =
			source && typeof source === "object" && "locale" in source
				? String((source as { locale?: unknown }).locale)
				: undefined;
		if (isLocale(value)) return value;
	}
	return defaultLocale;
};

const makeMeta = (
	title: string,
	description: string,
	locale: Locale,
	options: { path: string; image?: string; robots?: Robots },
) => {
	const url = new URL(options.path, SITE_ORIGIN).toString();
	const tags: ReturnType<MetaFunction> = [
		{ title },
		{ name: "description", content: description },
		{ name: "robots", content: options.robots ?? "index,follow" },
		...socialMeta({ title, description, url, image: options.image ?? DEFAULT_IMAGE, locale }),
	];

	return tags;
};

export const createLocalizedMeta =
	(options: LocalizedMetaOptions): MetaFunction =>
	({ data, matches }) => {
		const locale = localeFromData(data, matches.find(match => match.id === "root")?.data);
		return makeMeta(options.title(locale), options.description(locale), locale, options);
	};

export const createTranslatedMeta =
	(options: TranslatedMetaOptions): MetaFunction =>
	({ data, matches }) => {
		const locale = localeFromData(data, matches.find(match => match.id === "root")?.data);
		const t = createTranslator(getMessages(locale));
		return makeMeta(t(options.titleKey), t(options.descriptionKey), locale, options);
	};
